import React, { useState } from 'react'; // <-- Imported useState for collapse functionality
import { FaQuestionCircle, FaShippingFast, FaLeaf, FaWallet, FaChevronDown } from 'react-icons/fa';

// Define the FAQ data based on the Vermi Compost Barillery's Terms & Conditions
const faqData = [
  // --- Section 1: General & Products ---
  {
    category: "General & Products",
    icon: <FaLeaf className="text-3xl text-emerald-600" />,
    questions: [
      {
        q: "What kind of products does Vermi Compost Barillery offer?",
        a: "We specialize in natural and organic products, primarily including **vermi compost, vermi beds, and other organic gardening supplies.",
      },
      {
        q: "Are your vermi compost products consistent in quality?",
        a: "Since our products are naturally and organically made, there may be slight, natural variations in their color, texture, or moisture levels. However, we ensure all products meet our high quality standards for effectiveness.",
      },
      {
        q: "Is there an age requirement to use your services?",
        a: "Yes, you must be at least **18 years old to place an order. If you are under 18, you must have the consent of a parent or legal guardian.",
      },
    ]
  },
  
  // --- Section 2: Ordering & Payment ---
  {
    category: "Ordering & Payment",
    icon: <FaWallet className="text-3xl text-emerald-600" />,
    questions: [
      {
        q: "Can product prices change without notice?",
        a: "Yes, all product prices on our website are subject to change without prior notice. We reserve the right to correct any pricing errors before processing your order.",
      },
      {
        q: "Can I cancel my order after placing it?",
        a: "We reserve the right to refuse or cancel any order for reasons including product or service error, stock shortage, or suspicion of fraud. If you need to cancel, please contact us immediately, but cancellation is not guaranteed once processing begins.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept various payment methods, which typically include online payments and Cash On Delivery (COD), as detailed at checkout.",
      },
    ]
  },
  
  // --- Section 3: Shipping, Returns, & Refunds ---
  {
    category: "Shipping, Returns, & Refunds",
    icon: <FaShippingFast className="text-3xl text-emerald-600" />,
    questions: [
      {
        q: "What is your policy on returns and refunds for organic products?",
        a: "Due to the organic and perishable nature of vermi compost and related products, they are generally not eligible for return once delivered. Returns or refunds are only accepted if the product is delivered damaged or if you receive an incorrect item.",
      },
      {
        q: "What should I do if my order arrives damaged?",
        a: "If your product arrives damaged, you must notify us within the specified time limit (e.g., 24 hours) of delivery. You must provide appropriate photo proof of the damage for us to process an inspection and potential refund.",
      },
      {
        q: "Who is responsible for the risk during shipping?",
        a: "The risk of product delivery is generally upon the customer. Our responsibility for damage during shipping is limited to the extent outlined in our specific Shipping Policy.",
      },
    ]
  },
];

// --- New Accordion Component for individual Q&A ---
interface AccordionItemProps {
    q: string;
    a: string;
    isOpen: boolean;
    setOpen: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ q, a, isOpen, setOpen }) => (
    <div className="border-b border-gray-200">
        <button
            className="flex justify-between items-center w-full py-4 text-left font-semibold text-gray-800 hover:text-emerald-600 transition duration-150"
            onClick={setOpen}
            aria-expanded={isOpen}
        >
            <span className="text-lg">Q: {q}</span>
            <FaChevronDown 
                className={`w-4 h-4 text-emerald-600 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`}
            />
        </button>
        
        {/* Answer Content - Conditional Display */}
        <div 
            className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 py-2' : 'max-h-0 opacity-0'}`}
        >
            <p className="text-gray-600 text-base pb-4">
                A: {a}
            </p>
        </div>
    </div>
);
// --------------------------------------------------


const FAQPage: React.FC = () => {
    // State to track which question is currently open
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const toggleQuestion = (index: number) => {
        // If the same question is clicked, close it (set null). Otherwise, open the new one.
        setOpenIndex(openIndex === index ? null : index);
    };


    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-inter mt-32">
            {/* Header Section */}
            <div className="max-w-7xl mx-auto text-center mb-12">
                <h1 className="text-4xl sm:text-5xl font-extrabold text-emerald-700 mb-4">
                    Frequently Asked Questions (FAQ)
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                    Find answers to your questions about our organic products, ordering process, and policies.
                </p>
            </div>

            {/* FAQ Grid Layout */}
            {/* Changed from 3 columns to 1 for better focus on content, or you can keep the 3-column layout */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 gap-8 sm:gap-10"> 
            
                {faqData.map((section, sectionIndex) => (
                    // Using a full column width for the accordion style
                    <div key={sectionIndex} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                        
                        {/* Category Header */}
                        <div className="flex items-center space-x-3 mb-6 border-b-2 pb-3 border-emerald-100">
                            {section.icon}
                            <h2 className="text-2xl font-bold text-gray-800">
                                {section.category}
                            </h2>
                        </div>

                        {/* Questions List (Now using AccordionItem) */}
                        <div className="space-y-0">
                            {section.questions.map((item, index) => {
                                // Unique index for each item across all sections
                                const globalIndex = sectionIndex * 100 + index; 
                                return (
                                    <AccordionItem
                                        key={globalIndex}
                                        q={item.q}
                                        a={item.a}
                                        isOpen={openIndex === globalIndex}
                                        setOpen={() => toggleQuestion(globalIndex)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Final Call to Action */}
            <div className="text-center mt-16 p-8 bg-emerald-50 rounded-xl shadow-inner max-w-4xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-bold text-emerald-700 mb-4">
                    Still Have Questions?
                </h2>
                <p className="text-base sm:text-lg text-gray-700 mb-6">
                    If you couldn't find the answer you were looking for, our customer support team is ready to help.
                </p>
                <a
                    href={`mailto:info@vermicompostbarillery.com`}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 transition duration-150"
                >
                    Contact Support
                </a>
            </div>
        </div>
    );
};

export default FAQPage;